/**
 * Event tracker: links (file_download, external, email, phone, social).
 * @package AdvancedTrackingManager
 */

import type { AtmConfig } from '@lib/types';
import { getPageTitle, sendEvent } from './core';

const w = globalThis as unknown as Window;

const SOCIAL_LINK_HOSTS = [
  'linkedin.com',
  'www.linkedin.com',
  'youtube.com',
  'www.youtube.com',
  'youtu.be',
  'x.com',
  'twitter.com',
  'www.twitter.com',
  'instagram.com',
  'www.instagram.com',
  'facebook.com',
  'www.facebook.com',
  'fb.com',
  'fb.me',
  'tiktok.com',
  'www.tiktok.com',
];

const isSocialHost = (hostname: string): boolean => {
  if (!hostname) return false;
  const h = hostname.toLowerCase().replace(/^www\./, '');
  return SOCIAL_LINK_HOSTS.some((d) => {
    const dNorm = d.replace(/^www\./, '');
    return h === dNorm || h.endsWith(`.${dNorm}`);
  });
};

const hostToNetwork = (hostname: string): string => {
  if (!hostname) return '';
  const h = hostname.toLowerCase();
  if (h.includes('linkedin')) return 'linkedin';
  if (h.includes('youtube') || h.includes('youtu.be')) return 'youtube';
  if (h.includes('x.com') || h.includes('twitter')) return 'x';
  if (h.includes('instagram')) return 'instagram';
  if (h.includes('facebook') || h.includes('fb.')) return 'facebook';
  if (h.includes('tiktok')) return 'tiktok';
  return '';
};

export const trackDownloads = (
  config: AtmConfig,
  enable: { fileDownload: boolean },
  downloadExtensions: string[]
): void => {
  if (!enable.fileDownload) return;
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement)?.closest?.('a');
      if (!link?.href) return;
      if (Object.hasOwn(link.dataset ?? {}, 'atmExclude')) return;
      if (!URL.canParse(link.href, w.location.href)) return;
      const url = new URL(link.href, w.location.href);
      const pathname = url.pathname.toLowerCase();
      let extension = pathname.split('.').pop() ?? '';
      if (extension.includes('/')) extension = '';
      extension = extension.toLowerCase();
      const isDownloadAttr = link.hasAttribute('download');
      const isKnownExt = downloadExtensions.includes(extension);
      if (!isDownloadAttr && !isKnownExt) return;
      const fileExtension = isDownloadAttr ? (extension || 'download') : extension;
      const fileName = pathname ? (pathname.replace(/^\//, '').split('/').pop() ?? '') : '';
      const linkText =
        (link.textContent ?? '').trim().slice(0, 200) || (link.getAttribute('title') ?? '');
      sendEvent(
        'file_download',
        {
          file_extension: fileExtension,
          file_name: fileName,
          link_url: link.href,
          link_text: linkText,
          link_id: link.id ?? '',
          link_classes: typeof link.className === 'string' ? link.className : '',
          link_domain: url.hostname ?? '',
          page_title: getPageTitle(),
        },
        config
      );
    },
    true
  );
};

export const trackExternalLinks = (
  config: AtmConfig,
  enable: { externalLink: boolean }
): void => {
  if (!enable.externalLink) return;
  const currentHost = w.location.hostname ?? '';
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement)?.closest?.('a');
      if (!link?.href) return;
      if (Object.hasOwn(link.dataset ?? {}, 'atmExclude')) return;
      if (link.href.startsWith('mailto:') || link.href.startsWith('tel:')) return;
      if (!URL.canParse(link.href, w.location.href)) return;
      const host = new URL(link.href, w.location.href).hostname;
      if (!host || host === currentHost) return;
      sendEvent(
        'external_link',
        { link_url: link.href, outbound: true, page_title: getPageTitle() },
        config
      );
    },
    true
  );
};

export const trackEmailLinks = (config: AtmConfig, enable: { emailLink: boolean }): void => {
  if (!enable.emailLink) return;
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement)?.closest?.('a');
      if (!link || !link.href.startsWith('mailto:')) return;
      if (Object.hasOwn(link.dataset ?? {}, 'atmExclude')) return;
      sendEvent('email_link', { link_url: link.href, page_title: getPageTitle() }, config);
    },
    true
  );
};

export const trackPhoneLinks = (config: AtmConfig, enable: { phoneLink: boolean }): void => {
  if (!enable.phoneLink) return;
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement)?.closest?.('a');
      if (!link || !link.href.startsWith('tel:')) return;
      if (Object.hasOwn(link.dataset ?? {}, 'atmExclude')) return;
      sendEvent('phone_link', { link_url: link.href, page_title: getPageTitle() }, config);
    },
    true
  );
};

export const trackSocialLinks = (config: AtmConfig): void => {
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement)?.closest?.('a');
      if (!link?.href) return;
      if (Object.hasOwn(link.dataset ?? {}, 'atmExclude')) return;
      const currentHost = w.location.hostname ?? '';
      if (!URL.canParse(link.href, w.location.origin)) return;
      const host = new URL(link.href, w.location.origin).hostname;
      if (!host || host === currentHost) return;
      const isSocial =
        link.closest('.wp-block-social-links') ||
        link.closest('.wp-social-link') ||
        isSocialHost(host);
      if (!isSocial) return;
      sendEvent(
        'social_link',
        {
          link_url: link.href,
          page_title: getPageTitle(),
          social_network: hostToNetwork(host) || undefined,
        },
        config
      );
    },
    true
  );
};

const CTA_CONTAINER_SELECTORS = ['.wp-block-buttons', '.links', '.actions'] as const;
const HEADING_SELECTOR = 'h1,h2,h3,h4,h5,h6';

function isInsideCtaContainer(link: HTMLAnchorElement): boolean {
  for (const sel of CTA_CONTAINER_SELECTORS) {
    if (link.closest(sel)) return true;
  }
  return false;
}

/**
 * Text of the closest preceding heading (h1–h6) in document order, or empty string.
 */
function getClosestHeadingText(link: Element): string {
  const headings = document.querySelectorAll(HEADING_SELECTOR);
  let closest: Element | null = null;
  for (const h of headings) {
    if (h.compareDocumentPosition(link) === Node.DOCUMENT_POSITION_FOLLOWING) {
      closest = h;
    }
  }
  const text = closest ? (closest as HTMLElement).textContent?.trim() ?? '' : '';
  return text.slice(0, 200);
}

/**
 * Visible/label text of the link; "Image" if the link has no text (e.g. image-only button).
 */
function getCtaLinkText(link: HTMLAnchorElement): string {
  const raw = (link.textContent ?? '').trim().slice(0, 200);
  if (raw) return raw;
  const title = (link.getAttribute('title') ?? '').trim();
  if (title) return title.slice(0, 200);
  return 'Image';
}

export const trackCtaClick = (
  config: AtmConfig,
  enable: { ctaClick: boolean }
): void => {
  if (!enable.ctaClick) return;
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement)?.closest?.('a') as HTMLAnchorElement | null;
      if (!link?.href) return;
      if (Object.hasOwn(link.dataset ?? {}, 'atmExclude')) return;
      if (!isInsideCtaContainer(link)) return;
      const ctaContext = getClosestHeadingText(link);
      const linkText = getCtaLinkText(link);
      sendEvent(
        'cta_click',
        {
          link_url: link.href,
          link_text: linkText,
          page_title: getPageTitle(),
          cta_context: ctaContext,
        },
        config
      );
    },
    true
  );
};

export const trackSocialShare = (config: AtmConfig): void => {
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as HTMLElement)?.closest?.('.jsSocialShare, .jsShareLink') as HTMLElement | null;
      if (!el || Object.hasOwn((el as HTMLElement).dataset ?? {}, 'atmExclude')) return;
      const ds = (el as HTMLElement).dataset;
      let shareMethod = 'copy';
      let sharedUrl = '';
      if (el.classList.contains('jsSocialShare')) {
        shareMethod = (ds?.sharer ?? 'copy').trim() || 'copy';
        sharedUrl = (ds?.url ?? '').trim();
      } else if (el.classList.contains('jsShareLink')) {
        sharedUrl = (ds?.link ?? '').trim();
      }
      sendEvent(
        'social_share',
        {
          share_method: shareMethod,
          page_title: getPageTitle(),
          shared_url: sharedUrl || undefined,
        },
        config
      );
    },
    true
  );
};
